import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Lock } from "lucide-react";
import { api, errMsg, money } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Btn, Card, Field, Input, Loading, Pill, Select, Table, Textarea, labelCls } from "@/components/Ui";

const LINE_ORDER = ["supplier_price", "freight", "duty", "clearance", "finance", "inspection",
  "delivery", "other"];
const PCT_LABEL = "% of goods + freight";
const blankLine = { amount: "", currency: "AED" };

export default function CostSheet() {
  const { quoteId } = useParams();
  const { currencies = [] } = useCurrency() || {};
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);

  const rates = useMemo(
    () => currencies.reduce((a, c) => ({ ...a, [c.code]: c.rate }), { AED: 1 }), [currencies]);

  const calc = useMemo(() => {
    if (!sheet) return {};
    const base = sheet.base_currency || "AED";
    const toBase = (amount, currency) => {
      const from = rates[currency];
      const to = rates[base];
      if (!from || !to) return Number(amount || 0);
      return (Number(amount || 0) / from) * to;
    };
    const line = (k) => sheet.lines?.[k] || blankLine;
    const goods = toBase(line("supplier_price").amount, line("supplier_price").currency);
    const freight = toBase(line("freight").amount, line("freight").currency);
    const basis = goods + freight;
    const converted = {};
    let total = 0;
    LINE_ORDER.forEach((k) => {
      const l = line(k);
      const v = l.currency === "%" ? (basis * Number(l.amount || 0)) / 100
        : toBase(l.amount, l.currency);
      converted[k] = v;
      total += v;
    });
    const qty = Number(sheet.quantity || 0);
    const selling = Number(sheet.selling_price || 0);
    const overheads = Number(sheet.overheads || 0);
    const gross = selling - total;
    const net = gross - overheads;
    return {
      converted,
      total_landed_cost: total,
      unit_landed_cost: qty ? total / qty : null,
      margins: sheet.is_trader ? {
        gross_profit: gross, net_profit: net,
        net_margin_pct: selling ? Number(((net / selling) * 100).toFixed(2)) : null,
      } : null,
    };
  }, [sheet, rates]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/supplier/quotes/${quoteId}/cost-sheet`);
      setSheet({
        ...data,
        lines: LINE_ORDER.reduce((a, k) => ({ ...a, [k]: data.lines?.[k] || { ...blankLine } }), {}),
      });
    } catch (e) {
      toast.error(errMsg(e));
      setSheet(false);
    }
  }, [quoteId]);

  useEffect(() => { load(); }, [load]);

  if (sheet === null) return <Loading />;
  if (sheet === false) return <Card>This quotation is not available to your company.</Card>;

  const base = sheet.base_currency || "AED";
  const codes = currencies.length ? currencies.map((c) => c.code) : ["AED", "USD", "EUR", "GBP"];
  const setLine = (k, patch) =>
    setSheet((s) => ({ ...s, lines: { ...s.lines, [k]: { ...s.lines[k], ...patch } } }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/supplier/quotes/${quoteId}/cost-sheet`, {
        base_currency: base,
        quantity: sheet.quantity === "" || sheet.quantity == null ? null : Number(sheet.quantity),
        lines: LINE_ORDER.reduce((a, k) => ({
          ...a,
          [k]: { amount: Number(sheet.lines[k].amount || 0), currency: sheet.lines[k].currency || base },
        }), {}),
        is_trader: !!sheet.is_trader,
        selling_price: sheet.selling_price === "" || sheet.selling_price == null ? null : Number(sheet.selling_price),
        overheads: sheet.overheads === "" || sheet.overheads == null ? null : Number(sheet.overheads),
        notes: sheet.notes || null,
      });
      setSheet((s) => ({ ...s, ...data, lines: s.lines }));
      toast.success("Cost sheet saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const margins = calc.margins || {};
  return (
    <div data-testid="cost-sheet-page">
      <Link to="/supplier/quotes" className="text-xs font-semibold text-[#667788] hover:text-[#102A43]">
        ← Back to my quotes
      </Link>
      <Card className="mt-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-[#D9A441]" />
            <h2 className="font-display text-xl font-black tracking-tight text-[#102A43]">
              Cost sheet — {sheet.quote_number || "quotation"} · {sheet.part_number}
            </h2>
          </div>
          <Pill tone="warn" testId="cost-sheet-privacy">
            <Lock className="h-3.5 w-3.5" /> Private to your company
          </Pill>
        </div>
        <p className="mt-2 text-sm text-[#667788]">
          Buyers never see this sheet or any margin figure. Enter each cost in its own currency —
          everything is converted into your base currency at today&apos;s rate.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <Field label="Base currency">
            <Select data-testid="cs-base-currency" value={base} options={codes}
              onChange={(e) => setSheet({ ...sheet, base_currency: e.target.value })} />
          </Field>
          <Field label="Quantity">
            <Input data-testid="cs-quantity" type="number" min="0" value={sheet.quantity ?? ""}
              onChange={(e) => setSheet({ ...sheet, quantity: e.target.value })} />
          </Field>
          <Field label="Sheet type">
            <Select data-testid="cs-sheet-type" value={sheet.is_trader ? "trader" : "cost"}
              onChange={(e) => setSheet({ ...sheet, is_trader: e.target.value === "trader" })}
              options={[["cost", "Landed cost only"],
                        ["trader", "I'm a trader / reseller (show margins)"]]} />
          </Field>
        </div>

        <Table head={["Cost line", "Amount", "Currency / basis", `Converted (${base})`]}
          testId="cost-sheet-table">
          {LINE_ORDER.map((k) => (
            <tr key={k} data-testid={`cs-row-${k}`}>
              <td className="font-semibold text-[#102A43]">{sheet.line_labels?.[k] || k}</td>
              <td>
                <Input data-testid={`cs-amount-${k}`} type="number" step="0.01"
                  className="mt-0 max-w-[140px]" value={sheet.lines[k].amount}
                  onChange={(e) => setLine(k, { amount: e.target.value })} />
              </td>
              <td>
                <Select data-testid={`cs-currency-${k}`} className="mt-0 max-w-[160px]"
                  value={sheet.lines[k].currency}
                  onChange={(e) => setLine(k, { currency: e.target.value })}
                  options={[...codes, ["%", PCT_LABEL]]} />
              </td>
              <td className="text-right font-semibold" data-testid={`cs-converted-${k}`}>
                {money(calc.converted?.[k] ?? 0, base)}
              </td>
            </tr>
          ))}
          <tr className="bg-[#F5F7FA]">
            <td colSpan={3} className="font-display text-base font-black text-[#102A43]">
              Total landed cost
            </td>
            <td className="text-right font-display text-lg font-black text-[#D9A441]"
              data-testid="cs-total">
              {money(calc.total_landed_cost ?? 0, base)}
            </td>
          </tr>
          <tr>
            <td colSpan={3} className="font-semibold">Landed cost per unit</td>
            <td className="text-right font-semibold" data-testid="cs-unit">
              {calc.unit_landed_cost != null ? money(calc.unit_landed_cost, base) : "—"}
            </td>
          </tr>
        </Table>

        {sheet.is_trader && (
          <div className="mt-5 rounded-[14px] border border-[#DFE6ED] bg-[#F8FAFC] p-5"
            data-testid="cs-trader-block">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#D9A441]">
                Trader / reseller — internal only
              </p>
              <Pill tone="bad">Hidden from buyers and from all quote exports</Pill>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label={`Selling price (${base})`}>
                <Input data-testid="cs-selling-price" type="number" step="0.01"
                  value={sheet.selling_price ?? ""}
                  onChange={(e) => setSheet({ ...sheet, selling_price: e.target.value })} />
              </Field>
              <Field label={`Overheads / commission (${base})`}>
                <Input data-testid="cs-overheads" type="number" step="0.01"
                  value={sheet.overheads ?? ""}
                  onChange={(e) => setSheet({ ...sheet, overheads: e.target.value })} />
              </Field>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              {[["Gross profit", margins.gross_profit, "cs-gross"],
                ["Net profit", margins.net_profit, "cs-net"]].map(([l, v, id]) => (
                <div key={l} className="rounded-[12px] border border-[#DFE6ED] bg-white p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{l}</p>
                  <p className="mt-1 font-display text-xl font-black text-[#102A43]" data-testid={id}>
                    {v != null ? money(v, base) : "—"}
                  </p>
                </div>
              ))}
              <div className="rounded-[12px] border border-[#DFE6ED] bg-white p-4">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                  Net margin
                </p>
                <p className="mt-1 font-display text-xl font-black text-[#147D78]" data-testid="cs-margin">
                  {margins.net_margin_pct != null ? `${margins.net_margin_pct}%` : "—"}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-5">
          <label className={labelCls}>Internal notes</label>
          <Textarea data-testid="cs-notes" rows={3} value={sheet.notes || ""}
            onChange={(e) => setSheet({ ...sheet, notes: e.target.value })} />
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <Btn data-testid="cs-save-btn" loading={busy} onClick={save}>Save cost sheet</Btn>
          <Btn variant="ghost" data-testid="cs-recalc-btn" onClick={save}>Recalculate</Btn>
        </div>
      </Card>
    </div>
  );
}
